import { useMemo, useState } from 'react'
import { WatchQueryFetchPolicy } from '@apollo/client'
import AdvancedComboBox, { AdvancedComboBoxProps } from 'components/AdvancedComboBox'
import { Part, useFullPartSearchQuery } from '../../../../../../graphql'
import { PartDisplayInfo } from '../AddPartsForm'

export type PartPickerProps = {
	id?: string,
	debounceKey?: string,
	label?: string,
	onChange: ( id?: number, option?: Part ) => void,
	selectedPart?: Part | PartDisplayInfo,
	required?: boolean,
} & Partial<Omit<AdvancedComboBoxProps<Part>, 'onChange'>>

export default function PartPicker( { debounceKey, selectedPart, onChange, ...props }: PartPickerProps ): JSX.Element {
	const [ searchString, setSearchString ] = useState( '' )

	const queryParams = {
		context: { debounceKey },
		fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
		variables: {
			search: searchString,
			excludeEmpty: true
		},
	}

	const { loading, data } = useFullPartSearchQuery( queryParams )

	const normalizedData = useMemo( () => {
		if ( data ) {
			const companyOwned = data.fullPartSearch?.companyOwned ?? []
			const masterList = data.fullPartSearch?.masterList ?? []
			return [ ...companyOwned, ...masterList ] as Part[]
		} else {
			return [] as Part[]
		}
	}, [ data ] )

	return (
		<AdvancedComboBox
			setSelectedOption={ ( o?: Part ) => onChange( o?.id, o )}
			selectedOption={selectedPart as Part | undefined}
			data={normalizedData}
			getName={( part: Part ) => `${part.partNumber} - ${part.partClass.description}`}
			getSecondaryText={( part: Part ) => part.provider.name}
			setSearchString={setSearchString}
			isLoading={loading}
			{...props}
		/>
	)
}
